import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import FeedbackPanel from '../components/FeedbackPanel';
import { track } from '../utils/analytics';

import { CANVAS_API } from '../config';
const API = CANVAS_API;

export default function ContextualChat() {
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  
  // Get context passed from Decision Guru
  const { recommendation, canvas, challenge, lens, result } = location.state || {};

  useEffect(() => {
    // Add initial context message
    if (recommendation) {
      setMessages([
        {
          role: 'assistant',
          content: `I'm here to help you action this recommendation. I have your business context and can help with practical next steps, specific guidance, or work through any concerns. What would you like to focus on?`
        }
      ]);
    }
  }, [recommendation]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      // Pass only data — the backend system prompt already defines the role,
      // injects principles, and sets the conversational tone. Sending
      // instructions here would override them.
      const contextString = [
        canvas ? `Business context: ${JSON.stringify(canvas)}` : '',
        challenge ? `Original challenge: ${challenge}` : '',
        lens ? `Decision lens: ${lens}` : '',
        recommendation ? `Recommendation being actioned: ${recommendation}` : '',
      ].filter(Boolean).join('\n');

      const response = await axios.post(`${API}/chat`, {
        messages: newMessages,
        context: contextString
      });

      if (response.data.success) {
        setMessages([...newMessages, { role: 'assistant', content: response.data.reply }]);
        track('contextual_chat_message_sent');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages([...newMessages, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]);
    }

    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f4f7' }}>
      <Header />
      
      <div style={{ display: 'flex', paddingTop: '4rem' }}>
        {/* Main Content */}
        <div style={{ flex: 1, padding: '2rem 1.5rem' }}>
          <div style={{ maxWidth: 800, margin: '0 auto' }}>
            
            {/* Header */}
            <div style={{ marginBottom: '1.5rem' }}>
              <button 
                onClick={() => navigate('/decision')}
                style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: '0.8rem', cursor: 'pointer', marginBottom: '1rem' }}
              >
                ← Back to recommendation
              </button>
              <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                Help me action this
              </h1>
              <p style={{ fontSize: '0.8rem', color: '#666', margin: '0.5rem 0', fontStyle: 'italic' }}>
                Conversational help with your specific recommendation. As we gather usage data, we'll build templates and structured resources for each type of recommendation.
              </p>
            </div>

            {/* Recommendation Summary Card */}
            {recommendation && (
              <div style={{ 
                background: 'white', 
                borderRadius: 12, 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                marginBottom: '1.5rem',
                border: '1px solid #e8e8f0'
              }}>
                <div style={{ padding: '1.25rem 1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>⭐</span>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', margin: 0 }}>
                      Recommended Action
                    </h3>
                  </div>
                  
                  {/* Summary (first 2 lines) */}
                  <div style={{ marginBottom: '0.75rem' }}>
                    <p style={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6, margin: 0 }}>
                      {recommendation.split('.').slice(0, 2).join('.') + (recommendation.split('.').length > 2 ? '.' : '')}
                    </p>
                    
                    {/* Expandable full recommendation */}
                    {recommendation.split('.').length > 2 && (
                      <details style={{ marginTop: '0.5rem' }}>
                        <summary style={{ 
                          cursor: 'pointer', 
                          fontSize: '0.75rem', 
                          color: '#1D9E75', 
                          fontWeight: 600,
                          listStyle: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.3rem'
                        }}>
                          <span style={{ fontSize: '0.8rem' }}>▼</span> Show full recommendation
                        </summary>
                        <div style={{ 
                          marginTop: '0.75rem', 
                          padding: '0.75rem', 
                          background: '#f8f9fc', 
                          borderRadius: 6,
                          fontSize: '0.85rem',
                          color: '#333',
                          lineHeight: 1.6
                        }}>
                          {recommendation}
                        </div>
                      </details>
                    )}
                  </div>

                  {/* Confidence Section */}
                  {result?.confidence && (
                    <div style={{ 
                      padding: '0.75rem', 
                      background: '#f8f9fc', 
                      borderRadius: 6,
                      fontSize: '0.8rem',
                      color: '#555',
                      borderTop: '1px solid #f0f0f0',
                      marginTop: '0.75rem'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 700, 
                          textTransform: 'uppercase', 
                          padding: '2px 8px', 
                          borderRadius: 20, 
                          background: result.confidence.level === 'high' ? '#1D9E75' : result.confidence.level === 'medium' ? '#BA7517' : '#A32D2D', 
                          color: 'white'
                        }}>
                          {result.confidence.level}
                        </span>
                        <strong>confidence in this recommendation</strong>
                      </div>
                      <p style={{ margin: '0 0 0.75rem', lineHeight: 1.5 }}>
                        {result.confidence.reason}
                      </p>
                      
                      {result.confidence.level !== 'high' && (
                        <button
                          onClick={() => navigate('/decision')}
                          style={{
                            padding: '0.35rem 0.65rem',
                            borderRadius: 6,
                            background: '#BA7517',
                            color: 'white',
                            border: 'none',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          Improve context →
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Chat Container */}
            <div style={{ 
              background: 'white', 
              borderRadius: 12, 
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              height: '500px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              
              {/* Messages */}
              <div style={{ 
                flex: 1, 
                overflowY: 'auto', 
                padding: '1.5rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {messages.map((msg, i) => (
                  <div key={i} style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    padding: '0.75rem 1rem',
                    borderRadius: 12,
                    background: msg.role === 'user' ? '#1D9E75' : '#f8f9fc',
                    color: msg.role === 'user' ? 'white' : '#333',
                    fontSize: '0.85rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {msg.content}
                  </div>
                ))}
                
                {loading && (
                  <div style={{ alignSelf: 'flex-start', color: '#888', fontSize: '0.8rem', fontStyle: 'italic' }}>
                    GuruOne is thinking...
                  </div>
                )}
              </div>

              {/* Input */}
              <div style={{ 
                padding: '1rem 1.5rem', 
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Ask for specific guidance, next steps, or work through concerns..."
                  disabled={loading}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e0e0e0',
                    borderRadius: 8,
                    fontSize: '0.85rem',
                    fontFamily: 'inherit',
                    outline: 'none',
                    boxSizing: 'border-box',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#888' }}>
                    Press Enter to send (Shift+Enter for new line)
                  </span>
                  <button
                    onClick={sendMessage}
                    disabled={loading || !input.trim()}
                    style={{
                      padding: '0.75rem 1.25rem',
                      borderRadius: 8,
                      background: '#1D9E75',
                      color: 'white',
                      border: 'none',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                      opacity: loading || !input.trim() ? 0.6 : 1,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feedback Sidebar */}
        <div style={{ 
          width: '280px', 
          background: 'white', 
          borderLeft: '1px solid #e8e8f0',
          padding: '2rem 1.5rem'
        }}>
          <div style={{ position: 'sticky', top: '2rem' }}>
            <h4 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1a1a2e', margin: '0 0 1rem' }}>
              Help Improve GuruOne
            </h4>
            <p style={{ fontSize: '0.75rem', color: '#666', margin: '0 0 1rem', lineHeight: 1.5 }}>
              Your feedback shapes the product. How useful is this contextual help?
            </p>
            
            <FeedbackPanel
              guru="Contextual Chat"
              promptSent={challenge || 'Help me action this'}
              responseReceived={recommendation || 'Contextual assistance'}
              contextData={{ recommendation, canvas, challenge, lens, result }}
              title="How did that go?"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
